/**
 * Utility functions for formatting addresses and street information
 */

import _ from 'lodash'

/**
 * District code to full name mapping
 */
export const districtByCode = {
    ET: "ETOBICOKE",
    EY: "EAST YORK",
    MK: "MARKHAM",
    NY: "NORTH YORK",
    SC: "SCARBOROUGH",
    VA: "VAUGHAN",
};

/**
 * Formats street information from incident data into a structured format
 * @param {Object} event - The incident event object
 * @param {string} event.prime_street - Primary street information
 * @param {string} event.cross_streets - Cross streets information
 * @returns {Object} - Formatted street information with primary, secondary, and district
 */
export const formatStreets = (event) => {
    const raw_prime_street = _.isEmpty(event.prime_street) ? '' : event.prime_street.trim();
    const prime_street_components = raw_prime_street.split(", ");
    let district = null;
    if (prime_street_components.length === 2) {
        district = districtByCode[prime_street_components.at(-1)];
    }
    const prime_street = prime_street_components[0];

    const cross_streets = _.chain(event.cross_streets.split("/") || "")
        .map(cross_street => {
            return cross_street.trim()
        })
        .filter(cross_street => {
            return !_.isEmpty(cross_street) // Includes a value of 1, btw.
        })
        .value();

    const allStreets = []
    if (prime_street) allStreets.push(prime_street)
    if (cross_streets && cross_streets.length) allStreets.push(...cross_streets)

    if (allStreets.length === 0) {
        return { primary: 'N/A', secondary: null, district: district }
    } else if (allStreets.length === 1) {
        return { primary: allStreets[0], secondary: null, district: district }
    } else if (allStreets.length === 2) {
        return { primary: `${allStreets[0]} & ${allStreets[1]}`, secondary: null, district: district }
    } else {
        return {
            primary: allStreets[0],
            secondary: `b/w ${allStreets[1]} & ${allStreets[2]}`,
            district: district
        }
    }
}

/**
 * Formats location information for display in popovers and simple contexts
 * @param {Object} incident - The incident data object
 * @param {string} incident.primary_street - Primary street
 * @param {string} incident.secondary_street - Secondary street (optional)
 * @param {string} incident.district - District (optional)
 * @returns {string} - Formatted location string
 */
export const formatLocation = (incident) => {
    if (!incident) return 'N/A'

    const parts = []
    if (incident.primary_street) parts.push(incident.primary_street)
    if (incident.secondary_street) parts.push(incident.secondary_street)
    if (incident.district) parts.push(`(${incident.district})`)

    return parts.join(' ')
}

/**
 * Formats location using the comprehensive formatStreets function
 * @param {Object} incident - The incident data object
 * @returns {string} - Formatted location string using the comprehensive formatting
 */
export const formatLocationComprehensive = (incident) => {
    if (!incident) return 'N/A'

    const formatted = formatStreets(incident)
    const parts = []

    if (formatted.primary) parts.push(formatted.primary)
    if (formatted.secondary) parts.push(formatted.secondary)
    if (formatted.district) parts.push(`(${formatted.district})`)

    return parts.join(' ')
}
